import { createEnum } from "@redotech/zod-util/zod-enum";

/**
 * Types of photography styles for AI image generation
 */
export enum ShootType {
  STUDIO = "studio",
  LIFESTYLE = "lifestyle",
  FLAT_LAY = "flat-lay",
  DETAIL = "detail",
  PACKAGING = "packaging",
}

export const ShootTypeSchema = createEnum(ShootType);

export const SHOOT_TYPE_LABELS: Record<ShootType, string> = {
  [ShootType.STUDIO]: "Studio",
  [ShootType.LIFESTYLE]: "Lifestyle",
  [ShootType.FLAT_LAY]: "Flat Lay",
  [ShootType.DETAIL]: "Detail",
  [ShootType.PACKAGING]: "Packaging",
};

export const SHOOT_TYPE_DESCRIPTIONS: Record<ShootType, string> = {
  [ShootType.STUDIO]: "Product isolated with clean simple backgrounds.",
  [ShootType.LIFESTYLE]: "Context imagery showing product in use.",
  [ShootType.FLAT_LAY]: "Top-down compositions with other items.",
  [ShootType.DETAIL]: "Close up macro images showing fine-details.",
  [ShootType.PACKAGING]: "Product in packaging or being unboxed.",
};

/**
 * Status of the image optimization workflow.
 */
export enum ImageOptimizationStatus {
  PENDING = "pending",
  GENERATING = "generating",
  CROPPING = "cropping",
  UPSCALING = "upscaling",
  SAVING = "saving",
  COMPLETED = "completed",
  FAILED = "failed",
}

export const ImageOptimizationStatusSchema = createEnum(
  ImageOptimizationStatus,
);

export const IMAGE_OPTIMIZATION_STATUS_LABELS: Record<
  ImageOptimizationStatus,
  string
> = {
  [ImageOptimizationStatus.PENDING]: "Preparing...",
  [ImageOptimizationStatus.GENERATING]: "Generating product images with AI",
  [ImageOptimizationStatus.CROPPING]: "Processing generated images",
  [ImageOptimizationStatus.UPSCALING]: "Upscaling images to high resolution",
  [ImageOptimizationStatus.SAVING]: "Saving optimized images",
  [ImageOptimizationStatus.COMPLETED]: "Complete!",
  [ImageOptimizationStatus.FAILED]: "Generation failed",
};

/**
 * Configuration for each optimization step's progress calculation
 */
export const IMAGE_OPTIMIZATION_STEP_CONFIG: Record<
  ImageOptimizationStatus,
  { weight: number; hasImageProgress: boolean }
> = {
  [ImageOptimizationStatus.PENDING]: { weight: 2, hasImageProgress: false },
  [ImageOptimizationStatus.GENERATING]: { weight: 55, hasImageProgress: false },
  [ImageOptimizationStatus.CROPPING]: { weight: 5, hasImageProgress: true },
  [ImageOptimizationStatus.UPSCALING]: { weight: 30, hasImageProgress: true },
  [ImageOptimizationStatus.SAVING]: { weight: 8, hasImageProgress: true },
  [ImageOptimizationStatus.COMPLETED]: { weight: 0, hasImageProgress: false },
  [ImageOptimizationStatus.FAILED]: { weight: 0, hasImageProgress: false },
};

/**
 * Ordered list of optimization steps for progress calculation
 */
export const IMAGE_OPTIMIZATION_STEP_ORDER: ImageOptimizationStatus[] = [
  ImageOptimizationStatus.PENDING,
  ImageOptimizationStatus.GENERATING,
  ImageOptimizationStatus.CROPPING,
  ImageOptimizationStatus.UPSCALING,
  ImageOptimizationStatus.SAVING,
  ImageOptimizationStatus.COMPLETED,
];
